import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet } from 'react-router-dom';

import { useSession } from '@/features/auth/useSession';
import type { PublicUser } from '@/lib/schemas/user';

interface RequireRoleProps {
  roles: Array<PublicUser['role']>;
}

/**
 * Gate for the moderation routes.
 *
 * A reader without the role is sent to the 404 rather than to a "forbidden"
 * page: whether /admin exists is not something the app needs to confirm to
 * someone who cannot use it. The API enforces the same rule regardless — this
 * only decides what is worth rendering.
 */
export function RequireRole({ roles }: RequireRoleProps) {
  const { t } = useTranslation();
  const { user, isLoading } = useSession();

  if (isLoading) {
    return (
      <Stack spacing={2} aria-busy="true" aria-label={t('common.checkingSession')}>
        <Skeleton variant="rounded" height={44} width="40%" />
        <Skeleton variant="rounded" height={200} />
      </Stack>
    );
  }

  if (!user || !roles.includes(user.role)) {
    return <Navigate to="/404" replace />;
  }

  return <Outlet />;
}
